import { EmptyState } from '@/components/ui/empty-state';
import type { AssessmentSummary, Checkpoint } from '@/lib/api/checkpoints';
import { checkpointLabel } from './checkpoint-status';
import { formatDate } from '@/lib/utils/format';

/**
 * VendorScoringTable — checkpoint × score grid with trend sparklines.
 *
 * Each row is one checkpoint; columns are (in order):
 *   - checkpoint id + name
 *   - target date
 *   - per-round score sparkline (oldest → newest)
 *   - latest round result (posture chip + score)
 *
 * Score derivation: we render the assessment's `overall_posture` as a
 * numeric proxy on the sparkline:
 *   green=3, yellow_divergent=2, red=1, grey/null=0
 *
 * This is a UI-level convenience for the sparkline only. The authoritative
 * per-criterion vendor claims live behind the criterion-scoring-history
 * modal (T-0116) — clicking any sparkline point opens that modal via
 * `data-open-crit-history`.
 *
 * Spec note: T-0103 says "vendor scoring at each checkpoint". The T-0067
 * backend exposes assessment summaries (overall_posture per round) but the
 * full vendor-claim/avero-verification dual-axis per criterion lives one
 * level deeper. The table here is the round-level rollup; T-0116's modal
 * surfaces the dual-axis below.
 */

export interface VendorScoringTableRow {
  checkpoint: Checkpoint;
  assessments: AssessmentSummary[];
}

export interface VendorScoringTableProps {
  /** Opens the scoring-round modal for a checkpoint. */
  onScore?: (checkpoint: Checkpoint) => void;
  rows: VendorScoringTableRow[];
  /** Optional handler when a sparkline cell is clicked. */
  onOpenHistory?: (checkpointId: string) => void;
}

export function VendorScoringTable({ rows, onScore, onOpenHistory }: VendorScoringTableProps) {
  if (rows.length === 0) {
    return (
      <EmptyState
        title="No checkpoints"
        description="Once IV&V checkpoints are configured, vendor scoring rounds will appear here as a trend per checkpoint."
      />
    );
  }
  return (
    <div className="overflow-x-auto rounded-card border border-ink-100">
      <table className="w-full border-collapse text-body-sm">
        <thead>
          <tr className="border-b border-ink-100 bg-paper-50 text-left">
            <th
              scope="col"
              className="px-3 py-2 text-meta font-medium uppercase tracking-wide text-ink-500"
            >
              Checkpoint
            </th>
            <th
              scope="col"
              className="w-[130px] px-3 py-2 text-meta font-medium uppercase tracking-wide text-ink-500"
            >
              Target
            </th>
            <th
              scope="col"
              className="w-[180px] px-3 py-2 text-meta font-medium uppercase tracking-wide text-ink-500"
            >
              Trend
            </th>
            <th
              scope="col"
              className="w-[160px] px-3 py-2 text-meta font-medium uppercase tracking-wide text-ink-500"
            >
              Latest
            </th>
            <th
              scope="col"
              className="w-[120px] px-3 py-2 text-meta font-medium uppercase tracking-wide text-ink-500"
            >
              Rounds
            </th>
            <th
              scope="col"
              className="w-[90px] px-3 py-2 text-meta font-medium uppercase tracking-wide text-ink-500"
            >
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ checkpoint, assessments }) => {
            const latest = assessments[assessments.length - 1];
            return (
              <tr key={checkpoint.checkpoint_id} className="border-b border-ink-100 align-middle">
                <td className="px-3 py-2">
                  <div className="flex items-baseline gap-2">
                    {checkpointLabel(checkpoint) ? (
                      <span className="font-mono text-meta text-ink-500">
                        {checkpointLabel(checkpoint)}
                      </span>
                    ) : null}
                    <span className="text-body-sm font-medium text-ink-900">{checkpoint.name}</span>
                  </div>
                </td>
                <td className="px-3 py-2 font-mono text-mono-sm text-ink-700">
                  {checkpoint.target_date ? formatDate(checkpoint.target_date) : '—'}
                </td>
                <td className="px-3 py-2">
                  <button
                    type="button"
                    onClick={() => onOpenHistory?.(checkpoint.checkpoint_id)}
                    data-open-crit-history={checkpoint.checkpoint_id}
                    className="block w-full rounded-card p-1 transition-colors hover:bg-paper-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
                    aria-label={`Open scoring history for ${checkpoint.name}`}
                  >
                    <Sparkline assessments={assessments} />
                  </button>
                </td>
                <td className="px-3 py-2">
                  {latest ? (
                    <LatestCell summary={latest} />
                  ) : (
                    <span className="text-meta text-ink-300">—</span>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-mono-sm text-ink-700">
                  {assessments.length}
                </td>
                <td className="px-3 py-2">
                  {onScore ? (
                    <button
                      type="button"
                      onClick={() => onScore(checkpoint)}
                      className="rounded-card border border-ink-200 bg-paper-0 px-2.5 py-1 text-meta font-medium text-ink-700 hover:bg-paper-50"
                    >
                      Score
                    </button>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function LatestCell({ summary }: { summary: AssessmentSummary }) {
  const posture = summary.overall_posture;
  const color =
    posture === 'green'
      ? 'text-posture-healthy'
      : posture === 'red'
        ? 'text-posture-critical'
        : posture === 'yellow_divergent'
          ? 'text-posture-at-risk'
          : 'text-posture-unassessed';
  return (
    <div className="space-y-0.5">
      <div className="flex items-center gap-1.5">
        <span className={`text-body-sm font-medium ${color}`}>
          {posture ? formatPosture(posture) : 'In progress'}
        </span>
        <span className="font-mono text-meta text-ink-500">R{summary.round_seq}</span>
      </div>
      <div className="font-mono text-meta text-ink-500">
        {summary.submitted_at ? formatDate(summary.submitted_at) : 'Open'}
      </div>
    </div>
  );
}

function formatPosture(p: NonNullable<AssessmentSummary['overall_posture']>): string {
  switch (p) {
    case 'green':
      return 'Green';
    case 'yellow_divergent':
      return 'Yellow';
    case 'red':
      return 'Red';
    case 'grey':
      return 'Grey';
  }
}

interface SparklineProps {
  assessments: AssessmentSummary[];
}

/**
 * Tiny inline-SVG sparkline. We avoid Recharts here on purpose — the
 * shape is fixed (single polyline over N points) and inline SVG keeps
 * the table row light. Recharts shows up on the pulse posture chart
 * where the interactive features earn the bundle weight.
 */
function Sparkline({ assessments }: SparklineProps) {
  if (assessments.length === 0) {
    return <span className="text-meta text-ink-300">No rounds yet</span>;
  }
  const W = 140;
  const H = 32;
  const PAD_X = 4;
  const PAD_Y = 4;
  const values = assessments.map(postureToScore);
  const max = 3;
  const min = 0;
  const xs = values.map((_, i) => {
    if (values.length === 1) return W / 2;
    return PAD_X + (W - 2 * PAD_X) * (i / (values.length - 1));
  });
  const ys = values.map((v) => {
    const norm = (v - min) / (max - min || 1);
    return H - PAD_Y - (H - 2 * PAD_Y) * norm;
  });
  const path = xs
    .map((x, i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)},${ys[i]!.toFixed(1)}`)
    .join(' ');
  const lastValue = values[values.length - 1]!;
  const trendColor = scoreToColor(lastValue);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width={W} height={H} role="img" aria-hidden="true">
      <line
        x1={PAD_X}
        x2={W - PAD_X}
        y1={H - PAD_Y - (H - 2 * PAD_Y) * 0.66}
        y2={H - PAD_Y - (H - 2 * PAD_Y) * 0.66}
        stroke="var(--ink-100)"
        strokeDasharray="2 3"
      />
      <path d={path} fill="none" stroke={trendColor} strokeWidth={1.5} />
      {xs.map((x, i) => (
        <circle key={i} cx={x} cy={ys[i]} r={2.2} fill={scoreToColor(values[i]!)} />
      ))}
    </svg>
  );
}

function postureToScore(a: AssessmentSummary): number {
  switch (a.overall_posture) {
    case 'green':
      return 3;
    case 'yellow_divergent':
      return 2;
    case 'red':
      return 1;
    case 'grey':
    case null:
    default:
      return 0;
  }
}

function scoreToColor(v: number): string {
  if (v >= 3) return 'var(--posture-healthy)';
  if (v >= 2) return 'var(--posture-at-risk)';
  if (v >= 1) return 'var(--posture-critical)';
  return 'var(--posture-unassessed)';
}
